import {
  ACTIVITIES_FETCH_API,
  ACTIVITIES_FETCH_API_FULFILLED,
  ACTIVITIES_FETCH_API_SET_BLOCKS,
  ACTIVITIES_FETCH_START,
  ACTIVITIES_LOAD_MORE,
  COMMENT_CREATE_API_FULFILLED,
  fetchActivities,
  setActivitiesDummyBlock
} from '../actions';
import {
  doesIssueHavePreviousActivities,
  getActivityBlocks,
  getCurrentActivitiesOffset,
  getCurrentUserInMcFormat,
  getCurrentPreviewIssueId
} from '../selectors';
import {
  ACTIVITY_THRESHOLD,
  ActivityBatchReadTypes,
  ACTIVITY_CHANGE_TYPES,
  activityChangeType
} from '../../constants/activity';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import findLastIndex from 'lodash/findLastIndex';
import {parseActivity} from '../../utils/activityHelper';

const shouldActivityBatchBeDisplayed = block =>
  (block.activityList ?? block.activities).some(activity => activity.changes.length > 0);

const concatNewBlocks = (stateBlocks, newBlocks, offset) => {
  const blocks = stateBlocks.asMutable();
  const blocksToAdd = [...newBlocks].reverse();

  if (offset === 0) {
    const sharedBlockIndex = blocks.findIndex(block => block.id === blocksToAdd[0].id);
    const stateBlocksToKeep = sharedBlockIndex === -1 ? [] : blocks.slice(0, sharedBlockIndex);
    return [...stateBlocksToKeep, ...blocksToAdd];
  } else {
    const sharedBlockIndex = findLastIndex(blocks, block => block.id === blocksToAdd[newBlocks.length - 1].id);
    const stateBlocksToKeep = blocks.slice(sharedBlockIndex + 1);
    return [...blocksToAdd, ...stateBlocksToKeep];
  }
};

const parseActivitiesToV1Structure = activities =>
  activities.map(({id, activity: {object: issueObject, verb: type, actor, published}}) => {
    const activityList = parseActivity([
      {
        type,
        datetime: published,
        changes: ACTIVITY_CHANGE_TYPES.reduce((acc, changeType) => {
          if (changeType in issueObject) {
            let change;

            try {
              change = issueObject[changeType];
              change.id = new Date(issueObject.updated_at).getTime();

              if (changeType === activityChangeType.ChangedBody) {
                change.id = issueObject.comment.id;
              }

              if (changeType === activityChangeType.ChangedCustomAttributes) {
                change = {...change, before: JSON.parse(change.before), after: JSON.parse(change.after)};
              }

              acc.push(change);
            } catch (e) {
              // ignore change
              console.warn(`Skipping activity log change which could not be parsed to JSON: ${change}`);
            }
          }

          return acc;
        }, [])
      }
    ]);

    return {
      id,
      activityList,
      actor,
      firstActivityDate: activityList[0].datetime
    };
  });

const areVisibleBatchesBelowThreshold = blocks => {
  const visibleBlocks = blocks.filter(shouldActivityBatchBeDisplayed);
  return visibleBlocks.length < ACTIVITY_THRESHOLD;
};

const pollActivitiesIfNeeded = (triggerId, triggerTimestamp, dispatchFetchActivities, blocks) => {
  let timePassedSinceChange;

  if (triggerId) {
    timePassedSinceChange = Date.now() - new Date(triggerTimestamp);
  }

  const shouldPollingLatestActivities =
    !!triggerId &&
    blocks.every(block => block.activityList[0].changes.every(change => change.id !== triggerId)) &&
    timePassedSinceChange < 30000; // limit polling

  if (shouldPollingLatestActivities) {
    const dynamicDelay = 200 + timePassedSinceChange / 10;
    setTimeout(() => dispatchFetchActivities(0), dynamicDelay);
  }
};

export default issuesV2Api => store => next => action => {
  switch (action.type) {
    case ACTIVITIES_FETCH_API_FULFILLED: {
      const state = store.getState();
      const stateBlocks = getActivityBlocks(state);
      const currentPreviewIssueId = getCurrentPreviewIssueId(state);

      /** Exit when issue in preview got changed before API returns data */
      if (currentPreviewIssueId !== action.meta.issueId) {
        break;
      }
      const {
        results: rawResults = [],
        pagination: {totalResults: total = 0, offset: resultsOffset = 0} = {}
      } = action.payload;
      const results = parseActivitiesToV1Structure(rawResults);

      const {issueId, readType, offset: requestOffset, triggerId, triggerTimestamp} = action.meta;
      const blocks = results.length > 0 ? concatNewBlocks(stateBlocks, results, requestOffset) : stateBlocks;
      const offset = readType === ActivityBatchReadTypes.PREVIOUS ? resultsOffset + results.length : blocks.length;

      store.dispatch({
        type: ACTIVITIES_FETCH_API_SET_BLOCKS,
        payload: {
          blocks,
          total,
          offset
        },
        meta: {
          issueId,
          readType
        }
      });

      // Store got updated after setting blocks
      const updatedState = store.getState();
      const updatedBlocks = getActivityBlocks(updatedState);
      const updatedOffset = getCurrentActivitiesOffset(updatedState);

      const dispatchFetchActivities = offsetArg =>
        store.dispatch(
          fetchActivities({
            issueId,
            readType,
            offset: offsetArg,
            triggerId,
            triggerTimestamp
          })
        );

      if (
        (areVisibleBatchesBelowThreshold(updatedBlocks) && doesIssueHavePreviousActivities(updatedState)) ||
        (updatedBlocks.length && updatedBlocks.every(block => !shouldActivityBatchBeDisplayed(block)))
      ) {
        dispatchFetchActivities(updatedOffset);
      }

      pollActivitiesIfNeeded(triggerId, triggerTimestamp, dispatchFetchActivities, blocks);
      break;
    }
    case ACTIVITIES_FETCH_START: {
      const {issueId, readType, offset} = action.payload;
      const {triggerId, triggerTimestamp} = action.meta ?? {};
      const state = store.getState();
      const currentPreviewIssueId = getCurrentPreviewIssueId(state);
      /** Exit when issue in preview got changed before API returns data */
      if (currentPreviewIssueId !== issueId) {
        break;
      }
      store.dispatch({
        type: ACTIVITIES_FETCH_API,
        meta: {issueId, readType, offset, triggerId, triggerTimestamp},
        payload: issuesV2Api.getActivities({
          issueId,
          offset
        })
      });
      break;
    }
    case ACTIVITIES_LOAD_MORE: {
      const state = store.getState();
      const offset = getCurrentActivitiesOffset(state);
      const {issueId} = action.payload;
      store.dispatch(
        fetchActivities({
          issueId,
          offset,
          readType: ActivityBatchReadTypes.PREVIOUS
        })
      );
      break;
    }
    case COMMENT_CREATE_API_FULFILLED: {
      let actor = action.payload.createdBy;

      const state = store.getState();
      const user = getCurrentUserInMcFormat(state);
      actor = {id: user.id, name: user.name};

      store.dispatch(
        setActivitiesDummyBlock({
          actor,
          firstActivityDate: action.payload.createdAt,
          activities: [
            {
              type: ISSUE_ATTRIBUTE_NAMES.COMMENT_BODY,
              changes: [{property: 'body', before: '', after: action.payload.body}]
            }
          ]
        })
      );
      store.dispatch(
        fetchActivities({
          issueId: action.meta.id,
          readType: ActivityBatchReadTypes.LATEST,
          triggerId: action.payload.id,
          triggerTimestamp: action.payload.updatedAt
        })
      );
      break;
    }
    default:
      break;
  }
  next(action);
};
